import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Link2, FileText } from "lucide-react";
import { API } from "../../context/AuthContext";

const display = "font-display";
const CHUNK_SIZE = 4 * 1024 * 1024;

const MODES = {
  legal: { title: "Ingest Raw Legal Record Text", placeholder: "Paste raw messy court records, pleadings, email threads, or hearing logs here..." },
  safety: { title: "Ingest Encrypted Incident Log Entry", placeholder: "Paste incident logs, messages, or documentation of events here..." },
  family: { title: "Ingest Family Milestone Data", placeholder: "Paste milestone notes, journal entries, or memory logs here..." },
};

const SUB_TABS = [
  { key: "text", label: "Text", icon: FileText },
  { key: "file", label: "Files", icon: Upload },
  { key: "link", label: "Link", icon: Link2 },
];

export default function IngestPanel({ mode, caseId, onExtracted, onFileQueued }) {
  const [subTab, setSubTab] = useState("text");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const activeMode = MODES[mode];

  const igniteText = async () => {
    if (!text.trim()) return toast.error("The portal needs raw text — paste your records first!");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/extract`, { text, context_note: note, case_id: caseId });
      onExtracted(res.data.events);
      setText("");
      const dupNote = res.data.duplicates_skipped > 0 ? ` (${res.data.duplicates_skipped} duplicate${res.data.duplicates_skipped === 1 ? "" : "s"} skipped)` : "";
      toast.success(`Professor Flux extracted ${res.data.count} event${res.data.count === 1 ? "" : "s"}!${dupNote}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Flux ingestion portal misfired. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const igniteLink = async () => {
    if (!url.trim()) return toast.error("Paste a link for Flux to fetch!");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ingest/link`, { url, context_note: note, case_id: caseId });
      onExtracted(res.data.events);
      setUrl("");
      toast.success(`Flux pulled ${res.data.count} event${res.data.count === 1 ? "" : "s"} from that link!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't process that link.");
    } finally {
      setLoading(false);
    }
  };

  const uploadOne = async (file) => {
    const init = await axios.post(`${API}/files/upload/init`, { filename: file.name, size: file.size });
    const { upload_id } = init.data;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", upload_id);
      fd.append("index", i);
      fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      await axios.post(`${API}/files/upload/chunk`, fd);
      setUploadProgress((p) => ({ ...p, pct: Math.round(((i + 1) / total) * 100) }));
    }
    const done = await axios.post(`${API}/files/upload/complete`, { upload_id, context_note: note, case_id: caseId });
    return done.data;
  };

  const igniteFile = async () => {
    let files = Array.from(fileInputRef.current?.files || []);
    if (files.length === 0) return toast.error("Choose one or more files for Flux to analyze!");
    if (files.length > 10) {
      toast.warning("Max 10 files per batch — uploading the first 10. Beam up the rest afterward!");
      files = files.slice(0, 10);
    }
    setLoading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setUploadProgress({ current: i + 1, total: files.length, pct: 0, name: files[i].name });
      try {
        const doc = await uploadOne(files[i]);
        onFileQueued(doc);
        ok += 1;
      } catch (e) {
        toast.error(`"${files[i].name}": ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    fileInputRef.current.value = "";
    setLoading(false);
    setUploadProgress(null);
    if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} beamed up! Flux is now reading/watching/listening...`);
  };

  const ignite = subTab === "text" ? igniteText : subTab === "link" ? igniteLink : igniteFile;

  return (
    <div className="bg-[#131f3a] border border-white/5 p-5 rounded-2xl shadow-xl space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300" data-testid="ingest-panel-title">{activeMode.title}</h3>
        <span className="text-[10px] font-mono bg-white/5 text-gray-400 px-2 py-0.5 rounded border border-white/5">AI Powered</span>
      </div>

      <div className="bg-gray-950 p-1 rounded-xl border border-white/5 flex gap-1">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key)}
              data-testid={`ingest-tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold transition-colors duration-200 ${subTab === t.key ? "bg-[#f59e0b]/15 text-[#f59e0b] border border-[#f59e0b]/30" : "text-gray-400 hover:text-white border border-transparent"}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === "text" && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="raw-input-textarea"
          className="w-full h-40 bg-gray-950 border border-white/5 rounded-xl p-3 text-xs font-mono text-gray-300 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200 leading-relaxed resize-none"
          placeholder={activeMode.placeholder}
        />
      )}

      {subTab === "link" && (
        <div className="space-y-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="link-input"
            className="w-full bg-gray-950 border border-white/5 rounded-xl p-3 text-xs font-mono text-gray-300 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200"
            placeholder="https://example.com/article-or-record"
          />
          <p className="text-[10px] text-gray-400 leading-relaxed">Flux fetches the page instantly and extracts every dated event he finds.</p>
        </div>
      )}

      {subTab === "file" && (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            data-testid="file-input"
            accept=".pdf,.pdfa,.png,.jpg,.jpeg,.heic,.heif,.webp,.tiff,.bmp,.mp3,.wav,.m4a,.ogg,.flac,.webm,.mp4,.mov,.qt,.avi,.mpeg,.mpg,.wmv,.mkv"
            className="w-full text-xs text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#f59e0b]/15 file:text-[#f59e0b] file:text-[11px] file:font-bold file:cursor-pointer bg-gray-950 border border-white/5 rounded-xl p-2"
          />
          <p className="text-[10px] text-gray-400 leading-relaxed">
            PDFs & images get built-in OCR · audio (≤25MB) & video (≤100MB) get transcribed · max 10 files per batch · files auto-rename chronologically.
          </p>
          {uploadProgress !== null && (
            <div className="w-full bg-gray-950 rounded-full h-2 border border-white/5 overflow-hidden" data-testid="upload-progress">
              <div className="h-full bg-[#f59e0b] transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      )}

      <div>
        <label className="text-[11px] text-gray-300 block font-bold uppercase mb-1">Sticky Context Note (AI Instruction Focus)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="context-note-input"
          className="w-full bg-gray-950 border border-white/5 rounded-lg p-2.5 text-xs text-gray-200 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200"
          placeholder="e.g., 'Extract spousal support adjustments and hearing dates'"
        />
      </div>

      <button
        onClick={ignite}
        disabled={loading}
        data-testid="ignite-btn"
        className={`w-full bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-60 disabled:cursor-not-allowed text-gray-950 ${display} font-bold py-3 px-4 rounded-xl text-xs uppercase tracking-wider transition-colors duration-200 active:scale-[0.98] shadow-lg shadow-[#f59e0b]/10 flex items-center justify-center gap-2`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {uploadProgress !== null ? `Beaming Up ${uploadProgress.current}/${uploadProgress.total}... ${uploadProgress.pct}%` : "Flux Is Bending Time..."}
          </>
        ) : (
          "Ignite Flux Ingestion Portal"
        )}
      </button>
    </div>
  );
}
